import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ShoppingCart, Search, Trash2, Plus, Minus, ArrowLeft,
  CheckCircle, AlertCircle, Loader, User, UserPlus, X,
  Banknote, Smartphone, CreditCard, FileCheck, Package,
  LayoutGrid, List, Warehouse, MapPin, Info, Tag, Barcode,
  Calendar, Truck, FileText, Gift
} from 'lucide-react';
import { getProduits, createVente, updateVente, finaliserVente, getVente, getConfiguration, getClients, createClient, getAllStockDistributions, getLoyaltyCoupons, consumeLoyaltyCoupon, getFournisseurs, ajouterProduitCommandeFournisseur } from '../services/api';
import { useBoutique } from '../context/BoutiqueContext';
import { useAuth } from '../context/AuthContext';
import BoutiqueSelector from '../components/BoutiqueSelector';
import './NouvelleVente.css';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)) + ' XOF';

const MODES_PAIEMENT = [
  { value: 'especes',        label: 'Espèces',      icon: Banknote    },
  { value: 'wave',           label: 'Wave',          icon: Smartphone  },
  { value: 'orange_money',   label: 'Orange Money',  icon: Smartphone  },
  { value: 'carte_bancaire', label: 'Carte',         icon: CreditCard  },
  { value: 'cheque',         label: 'Chèque',        icon: FileCheck   },
];

// Niveau de stock d'un produit : rupture (≤ 0), critique (≤ seuil d'alerte), ok
const SEUIL_ALERTE_DEFAUT = 10;
const niveauStock = (p) => {
  const stock = Number(p.quantiteStock) || 0;
  if (stock <= 0) return 'rupture';
  const seuil = p.seuilAlerte != null ? Number(p.seuilAlerte) : SEUIL_ALERTE_DEFAUT;
  return stock <= seuil ? 'critique' : 'ok';
};

// Texte normalisé pour la recherche : minuscules, sans accents
const normaliser = (s) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function NouvelleVente() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id'); // si présent → mode édition d'une vente en attente
  const { user } = useAuth();
  const { selectedBoutiqueId } = useBoutique();
  const searchRef = useRef(null);

  const [produits,       setProduits]       = useState([]);
  const [clients,        setClients]        = useState([]);
  const [panier,         setPanier]         = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [submitting,     setSubmitting]     = useState(false);
  const [toast,          setToast]          = useState(null);
  // Distribution stock par produit : { [produitId]: [{entrepotId, nom, quantiteStock}, ...] }
  const [distributions,  setDistributions]  = useState({});
  // Produit pour lequel on affiche le picker d'entrepôt (vente multi-emplacement)
  const [pickerProduit,  setPickerProduit]  = useState(null);
  // Produit pour lequel on affiche la fiche détaillée
  const [detailProduit,  setDetailProduit]  = useState(null);
  // Filtre / tri par niveau de stock : 'tous' | 'rupture' | 'critique' (sous le seuil, hors rupture)
  const [filtreStock,    setFiltreStock]    = useState('tous');
  // Ajout à la liste de commande fournisseur : { produit, fournisseurs: [ids], quantite }
  const [cmdModal,       setCmdModal]       = useState(null);
  const [fournisseurs,   setFournisseurs]   = useState(null);   // chargés à la 1re ouverture
  const [cmdBusy,        setCmdBusy]        = useState(false);
  // Quantité choisie sur la fiche détail avant ajout au panier
  const [detailQuantite, setDetailQuantite] = useState(1);

  const [searchTerm,     setSearchTerm]     = useState('');
  const [viewMode,       setViewMode]       = useState(() => localStorage.getItem('nv_view_mode') || 'grid');
  const [modeVente,      setModeVente]      = useState('comptant');
  const [modePaiement,   setModePaiement]   = useState('especes');
  const [montantPaye,    setMontantPaye]    = useState('');
  const [dateEcheance,   setDateEcheance]   = useState('');
  const [remise,         setRemise]         = useState(0);
  const [tauxTVA,        setTauxTVA]        = useState(18);
  const [appliquerTVA,   setAppliquerTVA]   = useState(true);
  const [venteSansStock, setVenteSansStock] = useState(false);
  const [dateVente,      setDateVente]      = useState(''); // antidatage (rôles admin)
  // Client
  const [clientSearch,   setClientSearch]   = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDropdown,   setShowDropdown]   = useState(false);
  const [showNewClient,  setShowNewClient]  = useState(false);
  const [newClient,      setNewClient]      = useState({ nom:'', telephone:'', email:'', adresse:'', limiteCredit:'' });

  const isAdmin = ['admin-entreprise', 'super-admin'].includes(user?.role);
  const canBackdate = ['admin-entreprise', 'admin-pro', 'admin-boutique', 'super-admin'].includes(user?.role);
  // Négociateur : peut fixer un prix au-dessus du prix de vente listé (les admins ont ce droit
  // implicitement). Tout le monde reste plancher sur le prix de revient (prix d'achat).
  const canNegotiate = !!user?.peutNegocier
    || ['admin-entreprise', 'admin-pro', 'admin-boutique', 'super-admin'].includes(user?.role);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadProduits(), loadTauxTVA(), loadClients(), loadDistributions()]);
      // Mode édition : charger la vente en attente et pré-remplir le panier
      if (editId) {
        try {
          const { data } = await getVente(editId);
          const v = data.data;
          if (v && v.statut === 'en_attente') {
            const lignes = (v.details || []).map(d => ({
              lineId: d.entrepot ? `${d.produit?._id || d.produit}__${d.entrepot}` : (d.produit?._id || d.produit),
              produit: d.produit, // doit être populé par getVente
              quantite: d.quantite,
              typeVente: d.typeVente,
              prixUnitaire: d.prixUnitaire,
              quantiteMultiplier: d.quantiteMultiplier || 1,
              nomConditionnement: d.nomConditionnement || (d.quantiteMultiplier > 1 ? '' : 'Unité'),
              prixCustom: false,
              entrepotId: d.entrepot || null,
              nomEntrepot: d.nomEntrepot || '',
              stockEntrepot: 0,
            }));
            setPanier(lignes);
            if (v.remise)  setRemise(v.remise);
            if (v.tauxTVA != null) { setTauxTVA(v.tauxTVA); setAppliquerTVA(v.tauxTVA > 0); }
            if (v.clientId) setSelectedClient(v.clientId);
            if (v.loyaltyCoupon?.code) setPendingCouponCode(v.loyaltyCoupon.code);
          } else if (v) {
            showToast('error', 'Cette vente n\'est plus modifiable');
          }
        } catch { showToast('error', 'Impossible de charger la vente'); }
      }
      setLoading(false);
    };
    init();
  }, [selectedBoutiqueId, editId]); // eslint-disable-line

  const loadDistributions = async () => {
    try {
      const { data } = await getAllStockDistributions();
      setDistributions(data.data || {});
    } catch { /* silencieux : le picker fallback proposera juste "stock global" */ }
  };

  const loadProduits = async () => {
    try {
      // forSale=true : la boutique peut vendre n'importe quel produit de l'entreprise
      // (stock physiquement dans les entrepôts — transfert auto à la vente)
      const { data } = await getProduits({ forSale: true });
      setProduits(data.data || []);
    } catch { showToast('error', 'Erreur chargement produits'); }
  };

  const loadClients = async () => {
    try { const res = await getClients(); setClients(res.data.data || []); } catch {}
  };

  const loadTauxTVA = async () => {
    try {
      const r1 = await getConfiguration('taux_tva');
      setTauxTVA(Number(r1?.data?.data ?? r1?.data ?? 18) || 18);
      try {
        const r2 = await getConfiguration('tva_par_default');
        const val = r2?.data?.data ?? r2?.data ?? true;
        setAppliquerTVA(val === true || val === 'true');
      } catch { setAppliquerTVA(true); }
      try {
        const r3 = await getConfiguration('mode_vente_defaut');
        const m = String(r3?.data?.data ?? r3?.data ?? 'comptant').toLowerCase();
        // Ne pas écraser le mode si on charge une vente en attente existante (edit)
        if (!editId && (m === 'credit' || m === 'comptant')) {
          setModeVente(m);
          if (m === 'credit') setMontantPaye('0');
        }
      } catch { /* défaut comptant */ }
      try {
        const r4 = await getConfiguration('autoriser_vente_sans_stock');
        const v = r4?.data?.data ?? r4?.data ?? false;
        setVenteSansStock(v === true || v === 'true');
      } catch { setVenteSansStock(false); }
    } catch { setTauxTVA(18); setAppliquerTVA(true); }
  };

  // Index de recherche produit (calculé une fois par chargement du catalogue) :
  // nom, référence, code-barres, description et champs personnalisés (nom + valeur),
  // en minuscules et sans accents.
  const indexRecherche = useMemo(() => {
    const m = new Map();
    for (const p of produits) {
      const parts = [p.nom, p.codeProduit, p.codeBarres, p.description];
      for (const c of p.champsPersonnalises || []) parts.push(c.cle, c.valeur);
      m.set(p._id, normaliser(parts.filter(Boolean).join(' ')));
    }
    return m;
  }, [produits]);

  // Produits après filtre boutique + recherche (base des compteurs rupture / critique)
  const produitsRecherche = useMemo(() => {
    let r = produits;
    if (selectedBoutiqueId && selectedBoutiqueId !== 'all')
      r = r.filter(p => p.boutiqueId === selectedBoutiqueId);
    // Tous les mots saisis doivent être présents (dans n'importe quel champ indexé)
    const mots = normaliser(searchTerm).split(/\s+/).filter(Boolean);
    if (mots.length) {
      r = r.filter(p => {
        const txt = indexRecherche.get(p._id) || '';
        return mots.every(m => txt.includes(m));
      });
    }
    return r;
  }, [produits, indexRecherche, selectedBoutiqueId, searchTerm]);

  const compteStock = useMemo(() => {
    let rupture = 0, critique = 0;
    for (const p of produitsRecherche) {
      const n = niveauStock(p);
      if (n === 'rupture') rupture++; else if (n === 'critique') critique++;
    }
    return { rupture, critique };
  }, [produitsRecherche]);

  // Filtre rupture / critique, triés du stock le plus bas au plus haut
  const produitsFiltered = useMemo(() => {
    if (filtreStock === 'tous') return produitsRecherche;
    return produitsRecherche
      .filter(p => niveauStock(p) === filtreStock)
      .sort((a, b) => (Number(a.quantiteStock) || 0) - (Number(b.quantiteStock) || 0));
  }, [produitsRecherche, filtreStock]);

  // ── Liste de commande fournisseur ──
  const ouvrirCommande = async (p) => {
    let liste = fournisseurs;
    if (!liste) {
      try {
        const { data } = await getFournisseurs(true);
        liste = data.data || [];
      } catch { liste = []; }
      setFournisseurs(liste);
    }
    // Fournisseur habituel du produit (champ texte) → présélection par nom
    const nomF = normaliser(p.fournisseur || '').trim();
    const f = nomF ? liste.find(x => normaliser(x.nom).trim() === nomF) : null;
    const seuil = p.seuilAlerte != null ? Number(p.seuilAlerte) : SEUIL_ALERTE_DEFAUT;
    const stock = Math.max(Number(p.quantiteStock) || 0, 0);
    setCmdModal({
      produit: p,
      fournisseurs: f ? [f._id] : [],
      // Suggestion : remonter à 2× le seuil d'alerte
      quantite: Math.max(seuil * 2 - stock, 1),
    });
  };

  const validerCommande = async (e) => {
    e.preventDefault();
    if (!cmdModal.fournisseurs.length) { showToast('error', 'Choisissez au moins un fournisseur'); return; }
    setCmdBusy(true);
    // Une ligne dans le brouillon de chaque fournisseur sélectionné ; le prix
    // d'achat est celui de la fiche produit (appliqué côté serveur).
    const nomDe = (fid) => (fournisseurs || []).find(f => f._id === fid)?.nom || 'Fournisseur';
    const ok = [], echecs = [];
    for (const fid of cmdModal.fournisseurs) {
      try {
        const { data } = await ajouterProduitCommandeFournisseur({
          fournisseur: fid,
          produit: cmdModal.produit._id,
          quantite: Number(cmdModal.quantite),
          typeVente: 'detail',
        });
        ok.push(`${nomDe(fid)} (${data.data?.numeroCommande || 'brouillon'})`);
      } catch (err) {
        echecs.push({ fid, msg: `${nomDe(fid)} : ${err.response?.data?.message || 'erreur'}` });
      }
    }
    setCmdBusy(false);
    if (echecs.length) {
      showToast('error', `${ok.length ? `Ajouté pour ${ok.join(', ')}. ` : ''}Échec — ${echecs.map(e => e.msg).join(' ; ')}`);
      // Ne garder cochés que les fournisseurs en échec, pour réessayer
      setCmdModal(m => ({ ...m, fournisseurs: echecs.map(e => e.fid) }));
    } else {
      showToast('success', `${cmdModal.produit.nom} ajouté à la commande de ${ok.join(', ')}`);
      setCmdModal(null);
    }
  };

  const clientsFiltered = useMemo(() => {
    if (!clientSearch.trim()) return clients.slice(0, 8);
    const s = clientSearch.toLowerCase();
    return clients.filter(c =>
      c.nom.toLowerCase().includes(s) || c.telephone.includes(clientSearch)
    ).slice(0, 8);
  }, [clients, clientSearch]);

  // ── Panier ──
  // Stock maxi pour une ligne : si la ligne a un entrepôt, on utilise son stock ;
  // sinon on retombe sur le stock global du produit.
  const lineStockMax = (item) => item.entrepotId
    ? (item.stockEntrepot || 0)
    : (item.produit.quantiteStock || 0);

  // Construit un identifiant stable pour la ligne (produit + entrepôt)
  const lineKey = (produitId, entrepotId) => entrepotId
    ? `${produitId}__${entrepotId}`
    : `${produitId}`;

  // Clic sur un produit du catalogue :
  // - 0 entrepôt → erreur (stock à 0)
  // - 1 entrepôt → ajout direct
  // - 2+ entrepôts → ouvre le picker pour choisir
  // `qte` permet d'ajouter plusieurs pièces d'un coup (depuis la fiche détail).
  const ajouterAuPanier = (produit, qte = 1) => {
    const stocks = distributions[produit._id] || [];
    const dispo = stocks.filter(s => s.quantiteStock > 0);

    if (dispo.length === 0) {
      // Fallback : pas de distribution chargée mais stock global > 0 → ajouter sans entrepôt
      if (produit.quantiteStock > 0) {
        return ajouterAvecEntrepot(produit, null, qte);
      }
      // Vente à découvert autorisée → on ajoute quand même (stock négatif au niveau produit)
      if (venteSansStock) {
        return ajouterAvecEntrepot(produit, null, qte);
      }
      showToast('error', 'Produit en rupture de stock');
      return;
    }
    if (dispo.length === 1) {
      return ajouterAvecEntrepot(produit, dispo[0], qte);
    }
    setPickerProduit({ produit, emplacements: dispo, quantite: qte });
  };

  const ajouterAvecEntrepot = (produit, entrepotEntry, qte = 1) => {
    const entId  = entrepotEntry?.entrepotId || null;
    const entNom = entrepotEntry?.nom || '';
    const entQte = entrepotEntry?.quantiteStock ?? produit.quantiteStock ?? 0;
    const lid    = lineKey(produit._id, entId);
    const ajout  = Math.max(1, parseInt(qte) || 1);

    const existant = panier.find(i => i.lineId === lid);
    if (existant) {
      const mult = existant.quantiteMultiplier || 1;
      if (!venteSansStock && (existant.quantite + ajout) * mult > lineStockMax(existant)) {
        showToast('error', 'Stock insuffisant dans cet entrepôt');
        return;
      }
      setPanier(p => p.map(i => i.lineId === lid ? { ...i, quantite: i.quantite + ajout } : i));
    } else {
      if (!venteSansStock && ajout > entQte) {
        showToast('error', `Stock insuffisant (max ${entQte})`);
        return;
      }
      const hasCond = produit.conditionnements?.length > 0;
      const px = produit.prixVenteDetail || produit.prixVente || 0;
      setPanier(p => [...p, {
        lineId: lid,
        produit, quantite: ajout, typeVente: 'detail',
        prixUnitaire: px, quantiteMultiplier: 1,
        nomConditionnement: hasCond ? 'Unité' : 'Détail',
        prixCustom: false,
        entrepotId:    entId,
        nomEntrepot:   entNom,
        stockEntrepot: entQte,
      }]);
    }
    setPickerProduit(null);
  };

  const retirerDuPanier = (lid) => setPanier(p => p.filter(i => i.lineId !== lid));
  const viderPanier     = ()    => setPanier([]);

  const changerQuantite = (lid, val) => {
    const newQ = Math.max(1, parseInt(val) || 1);
    const item = panier.find(i => i.lineId === lid);
    if (!item) return;
    if (!venteSansStock && newQ * (item.quantiteMultiplier || 1) > lineStockMax(item)) { showToast('error', 'Stock insuffisant'); return; }
    setPanier(p => p.map(i => i.lineId === lid ? { ...i, quantite: newQ } : i));
  };

  const changerConditionnement = (lid, cond) => {
    setPanier(p => p.map(i => {
      if (i.lineId !== lid) return i;
      if (!cond) return { ...i, quantiteMultiplier: 1, nomConditionnement: 'Unité', prixUnitaire: i.produit.prixVenteDetail || i.produit.prixVente, prixCustom: false };
      if (!venteSansStock && i.quantite * cond.quantiteBase > lineStockMax(i)) { showToast('error', 'Stock insuffisant'); return i; }
      return { ...i, quantiteMultiplier: cond.quantiteBase, nomConditionnement: cond.nom, prixUnitaire: cond.prixVente, prixCustom: false };
    }));
  };

  // Bornes prix d'une ligne (selon conditionnement choisi)
  const lineMinPrice = (item) => {
    // Conditionnement sélectionné : utiliser son prixAchat ; sinon le produit
    if (item.quantiteMultiplier > 1 && item.nomConditionnement) {
      const cond = item.produit.conditionnements?.find(c => c.nom === item.nomConditionnement);
      if (cond) return Number(cond.prixAchat) || 0;
    }
    return Number(item.produit.prixAchatDetail || item.produit.prixAchat) || 0;
  };
  const lineRefPrice = (item) => {
    if (item.quantiteMultiplier > 1 && item.nomConditionnement) {
      const cond = item.produit.conditionnements?.find(c => c.nom === item.nomConditionnement);
      if (cond) return Number(cond.prixVente) || 0;
    }
    return Number(item.produit.prixVenteDetail || item.produit.prixVente) || 0;
  };

  const changerPrix = (lid, val) => {
    const n = parseFloat(val);
    if (isNaN(n) || n < 0) return;
    setPanier(p => p.map(i => {
      if (i.lineId !== lid) return i;
      const ref      = lineRefPrice(i);
      const minPrice = lineMinPrice(i);

      // Plancher universel : prix de revient (prix d'achat) — pas de vente à perte
      if (minPrice > 0 && n < minPrice) {
        showToast('error', `Prix minimum : ${fmt(minPrice)} (prix de revient)`);
        return { ...i, prixUnitaire: minPrice, prixCustom: minPrice !== ref };
      }

      // Plafond : prix de vente listé, sauf rôle négociateur
      if (!canNegotiate && n > ref) {
        showToast('error', `Prix maximum : ${fmt(ref)} (rôle non négociateur)`);
        return { ...i, prixUnitaire: ref, prixCustom: false };
      }

      return { ...i, prixUnitaire: n, prixCustom: n !== ref };
    }));
  };

  // Changement d'entrepôt sur une ligne déjà au panier (dropdown inline)
  const changerEntrepotLigne = (lid, newEntrepot) => {
    setPanier(p => p.map(i => {
      if (i.lineId !== lid) return i;
      const newQte = newEntrepot?.quantiteStock ?? 0;
      // Si la quantité actuelle dépasse le stock du nouvel entrepôt, on la réduit
      const qte = Math.min(i.quantite, Math.max(1, Math.floor(newQte / (i.quantiteMultiplier || 1))));
      return {
        ...i,
        lineId: lineKey(i.produit._id, newEntrepot?.entrepotId),
        entrepotId:    newEntrepot?.entrepotId || null,
        nomEntrepot:   newEntrepot?.nom || '',
        stockEntrepot: newQte,
        quantite: qte,
      };
    }));
  };

  // ── Calculs ──
  const sousTotal = useMemo(() => panier.reduce((s, i) => s + i.prixUnitaire * i.quantite, 0), [panier]);
  const totalHT  = useMemo(() => panier.reduce((s, i) => s + i.prixUnitaire * i.quantite, 0) - Number(remise || 0), [panier, remise]);
  const tva      = useMemo(() => appliquerTVA ? (totalHT * (tauxTVA || 0)) / 100 : 0, [totalHT, appliquerTVA, tauxTVA]);
  const totalTTC = useMemo(() => Math.max(0, totalHT + tva), [totalHT, tva]);
  const monnaie  = useMemo(() => parseFloat(montantPaye || 0) - totalTTC, [montantPaye, totalTTC]);

  useEffect(() => {
    if (modeVente === 'comptant') setMontantPaye(totalTTC > 0 ? String(Math.ceil(totalTTC)) : '');
  }, [totalTTC, modeVente]); // eslint-disable-line

  // Avoir client appliqué sur cette vente (réduit le montant restant à payer)
  const [avoirApplique, setAvoirApplique] = useState(0);
  const avoirDispo = Number(selectedClient?.avoirDisponible) || 0;
  // Reset l'avoir si on change de client
  useEffect(() => { setAvoirApplique(0); }, [selectedClient?._id]);

  // ── Bons de fidélité (réductions échangées contre des points) ──
  const [loyaltyCoupons, setLoyaltyCoupons] = useState([]);
  const [appliedCoupon,  setAppliedCoupon]  = useState(null);
  const [pendingCouponCode, setPendingCouponCode] = useState(null); // bon à ré-appliquer (brouillon rechargé)

  const couponDiscount = (coupon, base) => {
    if (!coupon) return 0;
    const b = Math.max(0, Math.round(base || 0));
    const d = coupon.type === 'percentage' ? Math.round((b * coupon.valeur) / 100) : coupon.valeur;
    return Math.min(d, b);
  };

  // Charger les bons actifs du client sélectionné
  useEffect(() => {
    setAppliedCoupon(null);
    if (!selectedClient?._id) { setLoyaltyCoupons([]); return; }
    let cancelled = false;
    getLoyaltyCoupons(selectedClient._id)
      .then(({ data }) => { if (!cancelled) setLoyaltyCoupons(data.data || []); })
      .catch(() => { if (!cancelled) setLoyaltyCoupons([]); });
    return () => { cancelled = true; };
  }, [selectedClient?._id]);

  // Quand un bon est appliqué, la remise est pilotée par le bon (recalcul si le panier change)
  useEffect(() => {
    if (appliedCoupon) setRemise(couponDiscount(appliedCoupon, sousTotal));
  }, [appliedCoupon, sousTotal]); // eslint-disable-line

  const applyCoupon = (coupon) => {
    setAppliedCoupon(coupon);
    setRemise(couponDiscount(coupon, sousTotal));
  };
  const removeCoupon = () => { setAppliedCoupon(null); setRemise(0); };

  // Ré-applique le bon d'un brouillon rechargé dès que les bons du client sont chargés
  useEffect(() => {
    if (!pendingCouponCode || loyaltyCoupons.length === 0) return;
    const match = loyaltyCoupons.find(c => c.code === pendingCouponCode);
    if (match) { setAppliedCoupon(match); setPendingCouponCode(null); }
  }, [loyaltyCoupons, pendingCouponCode]);

  const detteApres   = useMemo(() => !selectedClient ? 0 : (selectedClient.detteActuelle || 0) + totalTTC - (parseFloat(montantPaye) || 0) - avoirApplique, [selectedClient, totalTTC, montantPaye, avoirApplique]);
  const creditDepasse = selectedClient?.limiteCredit != null && detteApres > selectedClient.limiteCredit;

  // ── Submit ──
  // mode: 'finaliser' (par défaut) | 'attente' (brouillon) | 'maj' (mise à jour d'une vente en attente)
  const handleSubmit = async (e, mode = 'finaliser') => {
    if (e?.preventDefault) e.preventDefault();
    if (panier.length === 0) { showToast('error', 'Le panier est vide'); return; }
    if (isAdmin && (!selectedBoutiqueId || selectedBoutiqueId === 'all')) { showToast('error', 'Sélectionnez une boutique'); return; }

    // Les contrôles client / paiement ne s'appliquent qu'à la finalisation
    if (mode === 'finaliser') {
      if (modeVente === 'credit') {
        if (!selectedClient && !showNewClient) { showToast('error', 'Sélectionnez ou créez un client'); return; }
        if (showNewClient && !newClient.nom.trim()) { showToast('error', 'Nom du client requis'); return; }
        if (creditDepasse) { showToast('error', 'Limite de crédit dépassée'); return; }
      } else {
        if ((parseFloat(montantPaye) || 0) + avoirApplique < totalTTC) { showToast('error', 'Montant payé insuffisant (avoir inclus)'); return; }
      }
    }

    setSubmitting(true);
    try {
      let clientId = null;
      if (selectedClient) {
        clientId = selectedClient._id;
      } else if (showNewClient && newClient.nom.trim()) {
        try {
          const r = await createClient({ nom: newClient.nom.trim(), telephone: newClient.telephone.trim() || undefined, email: newClient.email.trim() || undefined, limiteCredit: newClient.limiteCredit !== '' ? Number(newClient.limiteCredit) : null });
          clientId = r.data.data._id;
          await loadClients();
        } catch { if (modeVente === 'credit') throw new Error('Impossible de créer le client'); }
      }

      const detailsPayload = panier.map(i => ({
        produit: i.produit._id, quantite: i.quantite,
        typeVente: i.typeVente, prixUnitaire: i.prixUnitaire,
        quantiteMultiplier: i.quantiteMultiplier || 1,
        nomConditionnement: i.nomConditionnement || '',
        ...(i.entrepotId && { entrepot: i.entrepotId, nomEntrepot: i.nomEntrepot }),
      }));

      // Snapshot du bon de fidélité appliqué (pour affichage facture/ticket)
      const couponPayload = appliedCoupon ? {
        code: appliedCoupon.code,
        label: appliedCoupon.type === 'percentage'
          ? `-${appliedCoupon.valeur}%`
          : `-${new Intl.NumberFormat('fr-FR').format(appliedCoupon.valeur)} XOF`,
        type: appliedCoupon.type,
        valeur: appliedCoupon.valeur,
      } : null;

      // ── Branche 1 : enregistrer en attente (création OU mise à jour d'un brouillon)
      if (mode === 'attente' || mode === 'maj') {
        if (editId) {
          // Mise à jour d'une vente en attente existante
          await updateVente(editId, {
            details: detailsPayload,
            remise: parseFloat(remise) || 0,
            tauxTVA: appliquerTVA ? (parseFloat(tauxTVA) || 0) : 0,
            clientId: clientId || undefined,
            loyaltyCoupon: couponPayload,
          });
          showToast('success', 'Vente en attente mise à jour');
        } else {
          // Création d'un nouveau brouillon
          const venteData = {
            details: detailsPayload,
            enAttente: true,
            modePaiement: 'autre', // requis par le schéma, mais ignoré côté backend en mode attente
            montantPaye: 0,
            remise: parseFloat(remise) || 0,
            tauxTVA: appliquerTVA ? (parseFloat(tauxTVA) || 0) : 0,
          };
          if (clientId) venteData.clientId = clientId;
          if (couponPayload) venteData.loyaltyCoupon = couponPayload;
          if (isAdmin) venteData.boutiqueIdModal = selectedBoutiqueId;
          await createVente(venteData);
          showToast('success', 'Vente enregistrée en attente');
        }
        setTimeout(() => navigate('/ventes'), 1000);
        return;
      }

      // ── Branche 2 : finaliser une vente en attente existante
      if (editId) {
        // Avant de finaliser, sauvegarder les éventuelles modifs du panier
        await updateVente(editId, {
          details: detailsPayload,
          remise: parseFloat(remise) || 0,
          tauxTVA: appliquerTVA ? (parseFloat(tauxTVA) || 0) : 0,
          clientId: clientId || undefined,
          loyaltyCoupon: couponPayload,
        });
        await finaliserVente(editId, {
          modePaiement,
          montantPaye: parseFloat(montantPaye) || 0,
          clientId: clientId || undefined,
        });
        if (appliedCoupon) {
          try { await consumeLoyaltyCoupon(appliedCoupon.id, { venteId: editId }); } catch { /* non bloquant */ }
        }
        showToast('success', 'Vente finalisée !');
        setTimeout(() => navigate('/ventes'), 1000);
        return;
      }

      // ── Branche 3 : création directe (flux historique inchangé)
      const venteData = {
        details: detailsPayload,
        modePaiement,
        montantPaye: parseFloat(montantPaye) || 0,
        remise: parseFloat(remise) || 0,
        tauxTVA: appliquerTVA ? (parseFloat(tauxTVA) || 0) : 0,
      };
      if (couponPayload) venteData.loyaltyCoupon = couponPayload;
      if (clientId) venteData.clientId = clientId;
      if (avoirApplique > 0 && clientId) venteData.avoirApplique = avoirApplique;
      if (modeVente === 'credit' && dateEcheance) venteData.dateEcheance = dateEcheance;
      if (isAdmin) venteData.boutiqueIdModal = selectedBoutiqueId;
      if (canBackdate && dateVente) venteData.dateVente = dateVente;

      const resp = await createVente(venteData);
      // Consommer le bon de fidélité éventuellement appliqué
      if (appliedCoupon) {
        const venteId = resp?.data?.data?._id;
        try { await consumeLoyaltyCoupon(appliedCoupon.id, { venteId }); } catch { /* non bloquant */ }
      }
      showToast('success', 'Vente enregistrée !');
      setTimeout(() => navigate('/ventes'), 1200);
    } catch (err) {
      showToast('error', err.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="pos-loading"><Loader className="pos-spinner" size={28}/><span>Chargement…</span></div>
  );

  return (
    <div className="pos-page">

      {/* Toast */}
      {toast && (
        <div className={`pos-toast pos-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={14}/> : <AlertCircle size={14}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="pos-header">
        <div className="pos-header-left">
          <button className="pos-back" onClick={() => navigate('/ventes')}><ArrowLeft size={16}/></button>
          <span className="pos-header-title">{editId ? 'Modifier vente en attente' : 'Nouvelle vente'}</span>
        </div>
        <div className="pos-header-center">
          <div className="pos-search-wrap">
            <Search size={14} className="pos-search-ico"/>
            <input ref={searchRef} className="pos-search" type="text"
              placeholder="Rechercher : nom, référence, code-barres, description, champs personnalisés…"
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)} autoFocus/>
            {searchTerm && <button className="pos-search-clear" onClick={() => { setSearchTerm(''); searchRef.current?.focus(); }}><X size={12}/></button>}
          </div>
        </div>
        <div className="pos-header-right">
          {isAdmin && <BoutiqueSelector/>}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="pos-body">

        {/* ════ Produits ════ */}
        <div className="pos-catalog">
          <div className="pos-catalog-toolbar">
            <span className="pos-catalog-count">
              <Package size={13}/> {produitsFiltered.length} produit{produitsFiltered.length > 1 ? 's' : ''}
            </span>
            <div className="pos-stock-filter" role="group" aria-label="Filtrer par niveau de stock">
              <button type="button" className={`pos-stock-chip${filtreStock === 'tous' ? ' pos-stock-chip--active' : ''}`}
                onClick={() => setFiltreStock('tous')}>Tous</button>
              <button type="button" className={`pos-stock-chip pos-stock-chip--out${filtreStock === 'rupture' ? ' pos-stock-chip--active' : ''}`}
                onClick={() => setFiltreStock('rupture')} title="Produits en rupture, du stock le plus bas au plus haut">
                Rupture <span className="pos-stock-chip-n">{compteStock.rupture}</span>
              </button>
              <button type="button" className={`pos-stock-chip pos-stock-chip--low${filtreStock === 'critique' ? ' pos-stock-chip--active' : ''}`}
                onClick={() => setFiltreStock('critique')} title="Stock sous le seuil d'alerte (hors rupture), triés par stock croissant">
                Stock critique <span className="pos-stock-chip-n">{compteStock.critique}</span>
              </button>
            </div>
            <div className="pos-view-toggle" role="group" aria-label="Mode d'affichage">
              <button type="button"
                className={`pos-view-btn${viewMode === 'grid' ? ' pos-view-btn--active' : ''}`}
                onClick={() => { setViewMode('grid'); localStorage.setItem('nv_view_mode', 'grid'); }}
                title="Affichage grille">
                <LayoutGrid size={14}/> <span>Grille</span>
              </button>
              <button type="button"
                className={`pos-view-btn${viewMode === 'list' ? ' pos-view-btn--active' : ''}`}
                onClick={() => { setViewMode('list'); localStorage.setItem('nv_view_mode', 'list'); }}
                title="Affichage liste">
                <List size={14}/> <span>Liste</span>
              </button>
            </div>
          </div>
          {produitsFiltered.length === 0 ? (
            <div className="pos-catalog-empty">
              <Package size={36}/>
              <span>{searchTerm ? 'Aucun produit trouvé' : 'Aucun produit disponible'}</span>
            </div>
          ) : (
            <div className={`pos-grid pos-grid--${viewMode}`}>
              {produitsFiltered.map(p => {
                const linesForProduit = panier.filter(i => i.produit._id === p._id);
                const qty       = linesForProduit.reduce((s, l) => s + l.quantite, 0);
                const inCart    = qty > 0;
                const stock     = p.quantiteStock || 0;
                const niveau    = niveauStock(p);
                const out       = niveau === 'rupture';
                const low       = niveau === 'critique';
                const stocks    = distributions[p._id] || [];
                const dispoEnt  = stocks.filter(s => s.quantiteStock > 0);
                const multiEnt  = dispoEnt.length > 1;
                const champs    = p.champsPersonnalises || [];
                const champsTop = champs.slice(0, 2);
                const champsMore = Math.max(0, champs.length - 2);
                return (
                  <div key={p._id}
                    className={`pos-card${inCart ? ' pos-card--active' : ''}${low ? ' pos-card--low' : ''}${out && !venteSansStock ? ' pos-card--out' : ''}`}
                    onClick={() => ajouterAuPanier(p)}
                    aria-disabled={out && !venteSansStock}
                    title={out ? (venteSansStock ? 'Rupture — vente à découvert autorisée' : 'Rupture de stock') : (multiEnt ? `Dans ${dispoEnt.length} entrepôts` : undefined)}>
                    <div className="pos-card-av">{p.nom.charAt(0).toUpperCase()}</div>
                    <div className="pos-card-body">
                      <div className="pos-card-name">{p.nom}</div>
                      <div className="pos-card-code">
                        {p.codeProduit}
                        {p.codeBarres && <span className="pos-card-cb"> · {p.codeBarres}</span>}
                        {multiEnt && (
                          <span className="pos-card-multi" title={`${dispoEnt.length} entrepôts`}>
                            <Warehouse size={10}/> {dispoEnt.length}
                          </span>
                        )}
                      </div>
                      {champs.length > 0 && (
                        <div className="pos-card-champs">
                          {champsTop.map((c, i) => (
                            <span key={i} className="pos-card-champ" title={`${c.cle}: ${c.valeur}`}>
                              <span className="pos-card-champ-k">{c.cle}</span>
                              <span className="pos-card-champ-v">{c.valeur || '—'}</span>
                            </span>
                          ))}
                          {champsMore > 0 && (
                            <span className="pos-card-champ pos-card-champ--more"
                              onClick={(e) => { e.stopPropagation(); setDetailQuantite(1); setDetailProduit(p); }}>
                              +{champsMore}
                            </span>
                          )}
                        </div>
                      )}
                      <button
                        type="button"
                        className="pos-card-info"
                        onClick={(e) => { e.stopPropagation(); setDetailQuantite(1); setDetailProduit(p); }}
                        title="Voir toutes les informations">
                        <Info size={11}/>
                        <span>Détails</span>
                      </button>
                    </div>
                    <div className="pos-card-right">
                      <div className="pos-card-price">{fmt(p.prixVenteDetail || p.prixVente)}</div>
                      <div className={`pos-card-stock${out ? ' pos-card-stock--out' : low ? ' pos-card-stock--low' : ''}`}>
                        {out ? 'Rupture' : `Stock ${stock}`}
                      </div>
                      {(out || low) && (
                        <button type="button" className="pos-card-commander"
                          onClick={(e) => { e.stopPropagation(); ouvrirCommande(p); }}
                          title="Ajouter à la liste de commande fournisseur">
                          <Truck size={11}/> Commander
                        </button>
                      )}
                    </div>
                    {inCart && <div className="pos-card-badge">{qty}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ════ Caisse ════ */}
        <div className="pos-panel">

          {/* ── Panier ── */}
          <div className="pos-cart">
            <div className="pos-cart-head">
              <span className="pos-cart-title"><ShoppingCart size={14}/> Panier</span>
              {panier.length > 0 && (
                <button className="pos-cart-clear" onClick={viderPanier}><Trash2 size={12}/> Vider</button>
              )}
            </div>

            {panier.length === 0 ? (
              <div className="pos-cart-empty">
                <ShoppingCart size={22}/>
                <span>Ajouter des produits</span>
              </div>
            ) : (
              <div className="pos-cart-list">
                {panier.map(item => {
                  const stocksProduit = distributions[item.produit._id] || [];
                  const champs        = item.produit.champsPersonnalises || [];
                  return (
                    <div key={item.lineId} className="pos-item">
                      <div className="pos-item-name">
                        <span>{item.produit.nom}</span>
                        {item.produit.conditionnements?.length > 0 && (
                          <select className="pos-item-cond"
                            value={item.nomConditionnement || 'Unité'}
                            onChange={e => {
                              const val = e.target.value;
                              if (val === 'Unité') changerConditionnement(item.lineId, null);
                              else { const c = item.produit.conditionnements.find(c => c.nom === val); if (c) changerConditionnement(item.lineId, c); }
                            }}
                            onClick={e => e.stopPropagation()}>
                            <option value="Unité">Unité</option>
                            {item.produit.conditionnements.map((c, i) => (
                              <option key={i} value={c.nom}>{c.nom} ×{c.quantiteBase}</option>
                            ))}
                          </select>
                        )}
                      </div>

                      {/* Code produit */}
                      {(item.produit.codeProduit || item.produit.codeBarres) && (
                        <div className="pos-item-code" style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                          {item.produit.codeProduit}
                          {item.produit.codeBarres ? ` · ${item.produit.codeBarres}` : ''}
                        </div>
                      )}

                      {/* Champs personnalisés du produit (clé : valeur) */}
                      {champs.length > 0 && (
                        <div className="pos-item-champs">
                          {champs.map((c, i) => (
                            <span key={i} className="pos-item-champ">
                              <span className="pos-item-champ-cle">{c.cle}</span>
                              <span className="pos-item-champ-val">{c.valeur || '—'}</span>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Sélecteur d'entrepôt (visible si le produit a au moins un emplacement) */}
                      {stocksProduit.length > 0 && (
                        <div className="pos-item-warehouse">
                          <MapPin size={11}/>
                          <select
                            value={item.entrepotId || ''}
                            onChange={e => {
                              const entId = e.target.value;
                              const entry = stocksProduit.find(s => String(s.entrepotId) === entId) || null;
                              changerEntrepotLigne(item.lineId, entry);
                            }}
                            onClick={e => e.stopPropagation()}>
                            {stocksProduit.map(s => (
                              <option key={String(s.entrepotId)} value={String(s.entrepotId)}>
                                {s.nom} ({s.quantiteStock} disp.)
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Emplacements où se trouve ce produit dans l'entrepôt choisi (où aller le chercher) */}
                      {(() => {
                        const selEnt = stocksProduit.find(s => String(s.entrepotId) === String(item.entrepotId));
                        const emps = (selEnt?.emplacements || []).filter(e => e.emplacementId && e.quantiteStock > 0);
                        if (!emps.length) return null;
                        return (
                          <div className="pos-item-emplacements">
                            {emps.map((e, i) => (
                              <span key={i} className="pos-emp-chip" title={e.rayon ? `${e.rayon} › ${e.nom}` : e.nom}>
                                <MapPin size={9}/> {e.rayon ? `${e.rayon} › ` : ''}{e.nom} · <strong>{e.quantiteStock}</strong>
                              </span>
                            ))}
                          </div>
                        );
                      })()}

                      <div className="pos-item-controls">
                        <div className="pos-qty">
                          <button onClick={() => changerQuantite(item.lineId, item.quantite - 1)} disabled={item.quantite <= 1}><Minus size={10}/></button>
                          <input type="number" min="1" value={item.quantite}
                            onChange={e => changerQuantite(item.lineId, e.target.value)}/>
                          <button onClick={() => changerQuantite(item.lineId, item.quantite + 1)}><Plus size={10}/></button>
                        </div>
                        <input
                          type="number"
                          min={lineMinPrice(item) || 0}
                          max={canNegotiate ? undefined : (lineRefPrice(item) || undefined)}
                          step="1"
                          className={`pos-item-price${item.prixCustom ? ' pos-item-price--custom' : ''}`}
                          value={item.prixUnitaire}
                          onChange={e => changerPrix(item.lineId, e.target.value)}
                          title={`Prix unitaire — min ${fmt(lineMinPrice(item))}${canNegotiate ? '' : ` · max ${fmt(lineRefPrice(item))}`}`}
                          onClick={e => e.stopPropagation()}/>
                        <span className="pos-item-total">{fmt(item.prixUnitaire * item.quantite)}</span>
                        <button className="pos-item-del" onClick={() => retirerDuPanier(item.lineId)}><X size={12}/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Total ── */}
          {panier.length > 0 && (
            <div className="pos-total-block">
              {Number(remise) > 0 && (
                <div className="pos-total-row"><span>Remise</span><span>−{fmt(remise)}</span></div>
              )}
              {appliquerTVA && tva > 0 && (
                <div className="pos-total-row"><span>TVA {tauxTVA}%</span><span>{fmt(tva)}</span></div>
              )}
              <div className="pos-total-ttc">
                <span>Total</span>
                <span>{fmt(totalTTC)}</span>
              </div>
              {monnaie > 0 && (
                <div className="pos-monnaie"><CheckCircle size={13}/> Monnaie : <strong>{fmt(monnaie)}</strong></div>
              )}
            </div>
          )}

          {/* ── Formulaire scrollable ── */}
          <form id="pos-form" className="pos-form-scroll" onSubmit={handleSubmit}>

            {/* Mode vente */}
            <div className="pos-mode-row">
              <button type="button"
                className={`pos-mode-btn${modeVente === 'comptant' ? ' pos-mode-btn--active' : ''}`}
                onClick={() => { setModeVente('comptant'); setSelectedClient(null); setClientSearch(''); setShowNewClient(false); }}>
                Comptant
              </button>
              <button type="button"
                className={`pos-mode-btn pos-mode-btn--credit${modeVente === 'credit' ? ' pos-mode-btn--active' : ''}`}
                onClick={() => { setModeVente('credit'); setMontantPaye('0'); }}>
                À crédit
              </button>
            </div>

            {/* Antidatage — réservé aux rôles admin */}
            {canBackdate && (
              <div className="pos-opt-field" style={{ marginTop: 10 }}>
                <label>
                  Date de la vente <span style={{ color: '#94a3b8', fontWeight: 400 }}>(antidatage — laisser vide pour maintenant)</span>
                </label>
                <input type="datetime-local"
                  value={dateVente}
                  max={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                  onChange={e => setDateVente(e.target.value)}/>
              </div>
            )}

            {/* Client */}
            <div className="pos-client-wrap">
              {!showNewClient ? (
                <div className="pos-client-search">
                  {selectedClient ? (
                    <div className="pos-client-selected">
                      <div className="pos-client-av">{selectedClient.nom.charAt(0)}</div>
                      <div className="pos-client-info">
                        <span className="pos-client-name">{selectedClient.nom}</span>
                        <span className="pos-client-tel">{selectedClient.telephone}</span>
                        {modeVente === 'credit' && selectedClient.detteActuelle > 0 && (
                          <span className="pos-client-dette">Dette : {fmt(selectedClient.detteActuelle)}</span>
                        )}
                        {avoirDispo > 0 && (
                          <span className="pos-client-avoir">💳 Avoir : {fmt(avoirDispo)}</span>
                        )}
                      </div>
                      <button type="button" className="pos-client-clear" onClick={() => { setSelectedClient(null); setClientSearch(''); }}><X size={12}/></button>
                    </div>
                  ) : (
                    <div className="pos-client-field">
                      <User size={13} className="pos-client-ico"/>
                      <input type="text"
                        placeholder={modeVente === 'credit' ? 'Client * (nom ou tél.)' : 'Client (optionnel)'}
                        value={clientSearch}
                        onChange={e => { setClientSearch(e.target.value); setShowDropdown(true); }}
                        onFocus={() => setShowDropdown(true)}/>
                      <button type="button" className="pos-new-client-btn" onClick={() => setShowNewClient(true)} title="Nouveau client"><UserPlus size={13}/></button>
                    </div>
                  )}
                  {showDropdown && clientSearch && !selectedClient && clientsFiltered.length > 0 && (
                    <div className="pos-dropdown">
                      {clientsFiltered.map(c => (
                        <div key={c._id} className="pos-dropdown-item"
                          onMouseDown={() => { setSelectedClient(c); setClientSearch(c.nom); setShowDropdown(false); }}>
                          <div className="pos-dropdown-av">{c.nom.charAt(0)}</div>
                          <div>
                            <div className="pos-dropdown-name">{c.nom}</div>
                            <div className="pos-dropdown-sub">{c.telephone}{c.detteActuelle > 0 ? ` · Dette ${fmt(c.detteActuelle)}` : ''}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="pos-new-client">
                  <div className="pos-new-client-head">
                    <span>Nouveau client</span>
                    <button type="button" onClick={() => setShowNewClient(false)}><X size={13}/></button>
                  </div>
                  <div className="pos-new-client-row">
                    <input type="text" placeholder="Nom *" required={modeVente === 'credit'}
                      value={newClient.nom} onChange={e => setNewClient({...newClient, nom: e.target.value})}/>
                    <input type="tel" placeholder="Téléphone" value={newClient.telephone}
                      onChange={e => setNewClient({...newClient, telephone: e.target.value})}/>
                  </div>
                  {/* Crédit illimité par défaut (limiteCredit vide = null côté serveur) */}
                  <div className="pos-new-client-credit">
                    <label>
                      <input type="checkbox" checked={newClient.limiteCredit === ''}
                        onChange={e => setNewClient({ ...newClient, limiteCredit: e.target.checked ? '' : '0' })}/>
                      Crédit illimité
                    </label>
                    {newClient.limiteCredit !== '' && (
                      <input type="number" min="0" step="1000" placeholder="Limite (XOF)"
                        value={newClient.limiteCredit}
                        onChange={e => setNewClient({ ...newClient, limiteCredit: e.target.value === '' ? '0' : e.target.value })}/>
                    )}
                  </div>
                </div>
              )}
              {creditDepasse && (
                <div className="pos-alert"><AlertCircle size={12}/> Limite de crédit dépassée</div>
              )}
            </div>

            {/* Mode paiement */}
            <div className="pos-pay-modes">
              {MODES_PAIEMENT.map(m => {
                const Icon = m.icon;
                return (
                  <button key={m.value} type="button"
                    className={`pos-pay-btn${modePaiement === m.value ? ' pos-pay-btn--active' : ''}`}
                    onClick={() => setModePaiement(m.value)}>
                    <Icon size={13}/> {m.label}
                  </button>
                );
              })}
            </div>

            {/* Avoir client — affiché seulement si le client en a */}
            {selectedClient && avoirDispo > 0 && (
              <div className="pos-avoir-wrap">
                <div className="pos-avoir-head">
                  <span className="pos-avoir-label">💳 Avoir client disponible</span>
                  <span className="pos-avoir-balance">{fmt(avoirDispo)}</span>
                </div>
                <div className="pos-avoir-row">
                  <input
                    type="number"
                    min="0"
                    max={Math.min(avoirDispo, totalTTC)}
                    step="1"
                    value={avoirApplique}
                    onChange={e => {
                      const v = Math.max(0, Math.min(parseFloat(e.target.value) || 0, avoirDispo, totalTTC));
                      setAvoirApplique(v);
                      if (modeVente === 'comptant') {
                        // Réduit le montant reçu demandé
                        setMontantPaye(String(Math.max(0, Math.ceil(totalTTC - v))));
                      }
                    }}
                    placeholder="0"/>
                  <button type="button" className="pos-avoir-btn"
                    onClick={() => {
                      const v = Math.min(avoirDispo, totalTTC);
                      setAvoirApplique(v);
                      if (modeVente === 'comptant') setMontantPaye(String(Math.max(0, Math.ceil(totalTTC - v))));
                    }}>
                    Utiliser max
                  </button>
                  {avoirApplique > 0 && (
                    <button type="button" className="pos-avoir-btn pos-avoir-btn--clear"
                      onClick={() => {
                        setAvoirApplique(0);
                        if (modeVente === 'comptant') setMontantPaye(String(Math.ceil(totalTTC)));
                      }}>
                      <X size={11}/>
                    </button>
                  )}
                </div>
                {avoirApplique > 0 && (
                  <div className="pos-avoir-hint">
                    {fmt(avoirApplique)} déduits du total — reste à régler : <strong>{fmt(Math.max(0, totalTTC - avoirApplique))}</strong>
                  </div>
                )}
              </div>
            )}

            {/* Bons de fidélité — affichés si le client en a */}
            {selectedClient && loyaltyCoupons.length > 0 && (
              <div className="pos-avoir-wrap" style={{ borderColor: '#c7d2fe', background: '#eef2ff' }}>
                <div className="pos-avoir-head">
                  <span className="pos-avoir-label"><Gift size={12}/> Bons de fidélité</span>
                  <span className="pos-avoir-balance">{loyaltyCoupons.length} dispo</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                  {loyaltyCoupons.map(c => {
                    const active = appliedCoupon?.id === c.id;
                    const reduc = c.type === 'percentage' ? `-${c.valeur}%` : `−${fmt(c.valeur)}`;
                    return (
                      <div key={c.id}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                          padding: '6px 10px', borderRadius: 7, fontSize: 12,
                          border: active ? '1.5px solid #4f46e5' : '1px solid #e2e8f0',
                          background: active ? '#e0e7ff' : '#fff',
                        }}>
                        <div>
                          <strong>{c.rewardNom}</strong> <span style={{ color: '#4338ca', fontWeight: 700 }}>{reduc}</span>
                          <div style={{ color: '#64748b' }}>
                            {c.code} · réduction {fmt(couponDiscount(c, sousTotal))}
                            {c.expiresAt && ` · expire le ${new Date(c.expiresAt).toLocaleDateString('fr-FR')}`}
                          </div>
                        </div>
                        {active ? (
                          <button type="button" className="pos-avoir-btn pos-avoir-btn--clear" onClick={removeCoupon}>
                            <X size={11}/>
                          </button>
                        ) : (
                          <button type="button" className="pos-avoir-btn" onClick={() => applyCoupon(c)}>
                            Appliquer
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {appliedCoupon && (
                  <div className="pos-avoir-hint">
                    Bon {appliedCoupon.code} appliqué — remise de <strong>{fmt(couponDiscount(appliedCoupon, sousTotal))}</strong> sur la vente.
                  </div>
                )}
              </div>
            )}

            {/* Montant payé */}
            <div className="pos-amount-wrap">
              <label className="pos-amount-label">
                {modeVente === 'credit' ? 'Acompte versé (0 = crédit total)' : 'Montant reçu'}
                {avoirApplique > 0 && <span className="pos-opt"> (hors avoir)</span>}
              </label>
              <input
                className="pos-amount-input"
                type="number" min="0" step="1" required
                value={montantPaye}
                onChange={e => setMontantPaye(e.target.value)}/>
            </div>

            {/* Remise + TVA — toujours visibles */}
            <div className="pos-inline-opts">
              <div className="pos-opt-field">
                <label>Remise (XOF){appliedCoupon && <span className="pos-opt"> · bon {appliedCoupon.code}</span>}</label>
                <input type="number" min="0" placeholder="0" value={remise}
                  disabled={!!appliedCoupon}
                  title={appliedCoupon ? 'Remise pilotée par le bon de fidélité appliqué' : undefined}
                  onChange={e => setRemise(e.target.value)}/>
              </div>
              <div className="pos-opt-field">
                <label>TVA ({tauxTVA}%)</label>
                <button type="button"
                  className={`pos-tva-toggle${appliquerTVA ? ' pos-tva-toggle--on' : ''}`}
                  onClick={() => setAppliquerTVA(v => !v)}>
                  <span className="pos-tva-knob"/>
                  {appliquerTVA ? 'Activée' : 'Désactivée'}
                </button>
              </div>
            </div>

            {/* Date échéance crédit */}
            {modeVente === 'credit' && selectedClient && (
              <div className="pos-opt-field">
                <label>Date d'échéance</label>
                <input type="date" value={dateEcheance}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => setDateEcheance(e.target.value)}/>
              </div>
            )}

          </form>

          {/* ── Boutons en bas ── */}
          <div className="pos-submit-wrap">
            <button
              type="button"
              className="pos-submit--draft"
              disabled={panier.length === 0 || submitting}
              onClick={(e) => handleSubmit(e, editId ? 'maj' : 'attente')}>
              {submitting
                ? <><Loader size={13} className="pos-spin"/> Enregistrement…</>
                : (editId ? 'Enregistrer (rester en attente)' : 'Mettre en attente')}
            </button>
            <button type="submit" form="pos-form" className="pos-submit" disabled={panier.length === 0 || submitting || creditDepasse}>
              {submitting
                ? <><Loader size={16} className="pos-spin"/> Enregistrement…</>
                : <><CheckCircle size={16}/> {editId ? 'Finaliser' : 'Valider'} · {fmt(totalTTC)}</>}
            </button>
          </div>
        </div>
      </div>

      {/* ── Fiche détail produit (toutes les informations) ── */}
      {/* ── Ajout à la liste de commande fournisseur ── */}
      {cmdModal && (
        <div className="pos-picker-overlay" onClick={() => !cmdBusy && setCmdModal(null)}>
          <form className="pos-detail pos-cmd" onClick={e => e.stopPropagation()} onSubmit={validerCommande}>
            <div className="pos-picker-head">
              <div>
                <div className="pos-picker-title"><Truck size={14}/> Commander au fournisseur</div>
                <div className="pos-picker-sub">
                  {cmdModal.produit.nom} — stock {Number(cmdModal.produit.quantiteStock) || 0}
                  {cmdModal.produit.seuilAlerte != null ? ` · seuil ${cmdModal.produit.seuilAlerte}` : ''}
                </div>
              </div>
              <button type="button" className="pos-picker-close" onClick={() => setCmdModal(null)}><X size={14}/></button>
            </div>
            <div className="pos-detail-body">
              <div className="pos-cmd-field">
                <span>
                  Fournisseur(s) * <em className="pos-cmd-count">{cmdModal.fournisseurs.length} sélectionné{cmdModal.fournisseurs.length > 1 ? 's' : ''}</em>
                </span>
                {(fournisseurs || []).length === 0 ? (
                  <p className="pos-cmd-hint">Aucun fournisseur actif.</p>
                ) : (
                  <div className="pos-cmd-fournisseurs">
                    {fournisseurs.map(f => {
                      const coche = cmdModal.fournisseurs.includes(f._id);
                      return (
                        <label key={f._id} className={`pos-cmd-four${coche ? ' pos-cmd-four--on' : ''}`}>
                          <input type="checkbox" checked={coche}
                            onChange={() => setCmdModal(m => ({
                              ...m,
                              fournisseurs: coche ? m.fournisseurs.filter(x => x !== f._id) : [...m.fournisseurs, f._id],
                            }))}/>
                          {f.nom}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <label className="pos-cmd-field">
                <span>Quantité *</span>
                <input type="number" min="1" required value={cmdModal.quantite}
                  onChange={e => setCmdModal(m => ({ ...m, quantite: e.target.value }))}/>
              </label>
              <p className="pos-cmd-hint">
                Le produit est ajouté au brouillon de commande de chaque fournisseur coché (créé s'il n'existe pas).
                La commande reste à envoyer depuis <em>Commandes fournisseurs</em>.
              </p>
            </div>
            <div className="pos-detail-foot">
              <button type="submit" className="pos-detail-add" disabled={cmdBusy}>
                {cmdBusy ? <><Loader size={14} className="pos-spin"/> Ajout…</> : <><Plus size={14}/> {cmdModal.fournisseurs.length > 1 ? `Ajouter aux ${cmdModal.fournisseurs.length} commandes` : 'Ajouter à la commande'}</>}
              </button>
            </div>
          </form>
        </div>
      )}

      {detailProduit && (() => {
        const p = detailProduit;
        const stocks = distributions[p._id] || [];
        const champs = p.champsPersonnalises || [];
        const conds  = p.conditionnements || [];
        return (
          <div className="pos-picker-overlay" onClick={() => setDetailProduit(null)}>
            <div className="pos-detail" onClick={e => e.stopPropagation()}>
              <div className="pos-picker-head">
                <div>
                  <div className="pos-picker-title">
                    <Package size={14}/> {p.nom}
                  </div>
                  <div className="pos-picker-sub">{p.codeProduit}{p.codeBarres ? ` · ${p.codeBarres}` : ''}</div>
                </div>
                <button className="pos-picker-close" onClick={() => setDetailProduit(null)}><X size={14}/></button>
              </div>

              <div className="pos-detail-body">
                {p.image && (
                  <div className="pos-detail-image">
                    <img src={p.image} alt={p.nom} loading="lazy" decoding="async" onError={(e) => { e.target.style.display = 'none'; }}/>
                  </div>
                )}

                {p.description && (
                  <div className="pos-detail-section">
                    <div className="pos-detail-section-title">Description</div>
                    <div className="pos-detail-text">{p.description}</div>
                  </div>
                )}

                <div className="pos-detail-section">
                  <div className="pos-detail-section-title">Tarifs</div>
                  <div className="pos-detail-kv-grid">
                    {p.prixVenteDetail != null && (
                      <div className="pos-detail-kv"><span>Prix détail</span><strong>{fmt(p.prixVenteDetail)}</strong></div>
                    )}
                    {p.prixVente != null && (
                      <div className="pos-detail-kv"><span>Prix vente</span><strong>{fmt(p.prixVente)}</strong></div>
                    )}
                    {p.prixVenteGros > 0 && (
                      <div className="pos-detail-kv"><span>Prix gros</span><strong>{fmt(p.prixVenteGros)}</strong></div>
                    )}
                    {p.prixAchat > 0 && (
                      <div className="pos-detail-kv"><span>Prix d'achat</span><strong>{fmt(p.prixAchat)}</strong></div>
                    )}
                  </div>
                </div>

                <div className="pos-detail-section">
                  <div className="pos-detail-section-title">Stock</div>
                  <div className="pos-detail-kv-grid">
                    <div className="pos-detail-kv"><span>Quantité totale</span><strong>{p.quantiteStock || 0}</strong></div>
                    {p.seuilAlerte != null && (
                      <div className="pos-detail-kv"><span>Seuil d'alerte</span><strong>{p.seuilAlerte}</strong></div>
                    )}
                  </div>
                  {stocks.length > 0 && (
                    <div className="pos-detail-warehouses">
                      {stocks.map(s => {
                        const emps = (s.emplacements || []).filter(e => e.emplacementId && e.quantiteStock > 0);
                        return (
                          <div key={String(s.entrepotId)} className="pos-detail-warehouse">
                            <div className="pos-detail-warehouse-row">
                              <MapPin size={11}/>
                              <span className="pos-detail-warehouse-nom">{s.nom}</span>
                              <span className="pos-detail-warehouse-qte">{s.quantiteStock}</span>
                            </div>
                            {emps.length > 0 && (
                              <div className="pos-detail-emplacements">
                                {emps.map((e, i) => (
                                  <span key={i} className="pos-emp-chip">
                                    {e.rayon ? `${e.rayon} › ` : ''}{e.nom} · <strong>{e.quantiteStock}</strong>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {conds.length > 0 && (
                  <div className="pos-detail-section">
                    <div className="pos-detail-section-title">Conditionnements</div>
                    <div className="pos-detail-conds">
                      {conds.map((c, i) => (
                        <div key={i} className="pos-detail-cond">
                          <span className="pos-detail-cond-nom">{c.nom}</span>
                          <span className="pos-detail-cond-mult">×{c.quantiteBase}</span>
                          <span className="pos-detail-cond-prix">{fmt(c.prixVente)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(p.fournisseur || p.dateExpiration || p.nombrePiecesParCarton > 1) && (
                  <div className="pos-detail-section">
                    <div className="pos-detail-section-title">Autres</div>
                    <div className="pos-detail-kv-grid">
                      {p.fournisseur && (
                        <div className="pos-detail-kv"><Truck size={11}/><span>Fournisseur</span><strong>{p.fournisseur}</strong></div>
                      )}
                      {p.dateExpiration && (
                        <div className="pos-detail-kv"><Calendar size={11}/><span>Expiration</span><strong>{new Date(p.dateExpiration).toLocaleDateString('fr-FR')}</strong></div>
                      )}
                      {p.nombrePiecesParCarton > 1 && (
                        <div className="pos-detail-kv"><span>Pièces / carton</span><strong>{p.nombrePiecesParCarton}</strong></div>
                      )}
                    </div>
                  </div>
                )}

                {champs.length > 0 && (
                  <div className="pos-detail-section">
                    <div className="pos-detail-section-title">Champs personnalisés</div>
                    <div className="pos-detail-champs">
                      {champs.map((c, i) => (
                        <div key={i} className="pos-detail-champ">
                          <span className="pos-detail-champ-cle">{c.cle}</span>
                          <span className="pos-detail-champ-val">{c.valeur || '—'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="pos-detail-foot">
                <div className="pos-detail-qte">
                  <label>Quantité à vendre</label>
                  <div className="pos-detail-qte-controls">
                    <button type="button"
                      onClick={() => setDetailQuantite(q => Math.max(1, (parseInt(q) || 1) - 1))}
                      disabled={detailQuantite <= 1}>
                      <Minus size={12}/>
                    </button>
                    <input type="number" min="1" step="1"
                      value={detailQuantite}
                      onChange={e => setDetailQuantite(Math.max(1, parseInt(e.target.value) || 1))}/>
                    <button type="button"
                      onClick={() => setDetailQuantite(q => (parseInt(q) || 1) + 1)}>
                      <Plus size={12}/>
                    </button>
                  </div>
                </div>
                <button type="button" className="pos-detail-add"
                  onClick={() => {
                    const qte = Math.max(1, parseInt(detailQuantite) || 1);
                    ajouterAuPanier(p, qte);
                    setDetailProduit(null);
                  }}>
                  <Plus size={14}/> Ajouter {detailQuantite > 1 ? `${detailQuantite} pièces` : 'au panier'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Picker entrepôts (produit présent dans plusieurs emplacements) ── */}
      {pickerProduit && (
        <div className="pos-picker-overlay" onClick={() => setPickerProduit(null)}>
          <div className="pos-picker" onClick={e => e.stopPropagation()}>
            <div className="pos-picker-head">
              <div>
                <div className="pos-picker-title">
                  <Warehouse size={14}/> Choisir un emplacement
                </div>
                <div className="pos-picker-sub">{pickerProduit.produit.nom} · {pickerProduit.produit.codeProduit}</div>
              </div>
              <button className="pos-picker-close" onClick={() => setPickerProduit(null)}><X size={14}/></button>
            </div>
            <div className="pos-picker-list">
              {pickerProduit.emplacements.map(e => (
                <button key={String(e.entrepotId)}
                  className="pos-picker-item"
                  onClick={() => ajouterAvecEntrepot(pickerProduit.produit, e, pickerProduit.quantite || 1)}>
                  <div className="pos-picker-item-left">
                    <MapPin size={13}/>
                    <span className="pos-picker-nom">{e.nom}</span>
                  </div>
                  <span className="pos-picker-qte">{e.quantiteStock} dispo.</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
